import { useCookies } from 'react-cookie';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useResetRecoilState } from 'recoil';
import { useAtom } from 'jotai';
import posthog from 'posthog-js';
import { toast } from '../use-toast';

import {
  CLIENT_BASE_URL,
  APP_ROUTE,
  LOCAL_STORAGE_TYPES,
  COOKIE_KEYS,
} from '../../constants';
import API from '../../api';
import {
  onLogout as thirdPartyToolsLogout,
  setIdentity,
} from '../../helpers/external-tools-helper/index.ts';
import {
  AuthContainer,
  UserContainer,
  ClientContainer,
  PagesContainer,
  ApplicationContainer,
  ReportsContainer,
  TeamContainer,
  AddonContainer,
  BillingContainer,
} from '../../providers';
import { useGetBusiness } from './business';
// eslint-disable-next-line import/no-cycle
import { useGetPermissions, useGetUser } from './user';
import useLocalStorage from '../localstorage';
import { useAcceptInvite, useGetMembers } from './team';
import useAddonRedirect from '../use-addon-redirect';
import {
  ChartsState,
  DestinationsState,
  RawDestinationsState,
  useBillingInfo,
} from '../../states';
import { useNetwork } from '../use-network';
import { useIndexedDB } from '../indexed-db';
import { sidebarAtom } from '../../components/ui/sidebar';

const useSignOut = () => {
  const navigate = useNavigate();
  const { isAddon } = AddonContainer.useContainer();

  // eslint-disable-next-line no-unused-vars
  const [cookies, setCookie, removeCookie] = useCookies(['accessToken']);
  const {
    resetUserState,
    resetBusinessState,
    resetPermissions,
  } = UserContainer.useContainer();
  const { resetAuthState } = AuthContainer.useContainer();
  const { resetClientState } = ClientContainer.useContainer();
  const { resetPageState } = PagesContainer.useContainer();
  const { resetReportsState } = ReportsContainer.useContainer();
  const { resetTeamState } = TeamContainer.useContainer();
  const { resetBillingInfoState } = useBillingInfo();
  const {
    hardResetBillingState: resetBillingState,
  } = BillingContainer.useContainer();
  const resetCharts = useResetRecoilState(ChartsState);
  const resetDestination = useResetRecoilState(DestinationsState);
  const resetRawDestination = useResetRecoilState(RawDestinationsState);
  const { clear } = useIndexedDB({
    currentStore: '',
  });
  const [, setSidebarOpen] = useAtom(sidebarAtom);

  const signOut = (redirectOnSignout = true) => {
    // Remove the callbackUrl cookie to prevent stale redirects
    removeCookie('callbackUrl', { path: '/' });
    removeCookie(COOKIE_KEYS.ACCESS_TOKEN, { path: '/' });
    localStorage.removeItem('sidebar');
    setSidebarOpen(true); // Reset sidebar to expanded state
    resetAuthState();
    resetUserState();
    resetClientState();
    resetBusinessState();
    resetPermissions();
    resetPageState();
    resetReportsState();
    thirdPartyToolsLogout();
    resetTeamState();
    resetBillingState();
    resetBillingInfoState();
    resetCharts();
    resetDestination();
    resetRawDestination();
    clear();
    // clearAllChartPreferences(); // Clear multi-metric chart preferences
    posthog.reset(); // reset the Posthog session

    if (redirectOnSignout)
      if (isAddon) {
        navigate(`/${APP_ROUTE.ADDON}/${APP_ROUTE.LOGIN}`, { replace: true });
      } else {
        navigate(`/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`, { replace: true });
      }
  };

  return signOut;
};

const useInitializeApp = () => {
  const getUser = useGetUser();
  const getBusiness = useGetBusiness();
  const getPermissions = useGetPermissions();
  const signOut = useSignOut();
  const getMembers = useGetMembers();

  const {
    setNotification,
    setApplicationLoaded,
  } = ApplicationContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();

  const initializeApp = async () => {
    try {
      const user = await getUser();
      // set identity in our tracking libraries
      setIdentity(user);
      const isBusiness = await getBusiness({
        fields: 'accounts',
      });

      if (isBusiness) {
        await getPermissions(user.id);
      }
      if (userState?.business?.id) {
        await getMembers(userState?.business?.id);
      }
      setApplicationLoaded(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      setNotification({
        type: 'error',
        messageComponent: error.message,
      });
      signOut();
      setApplicationLoaded(true);
    }
  };
  return initializeApp;
};

const useAuthenticateUser = () => {
  const { updateAccessToken } = AuthContainer.useContainer();
  // eslint-disable-next-line no-unused-vars
  const [_cookies, setCookie] = useCookies(['accessToken']);
  const initializeApp = useInitializeApp();
  const { set } = useLocalStorage();
  const acceptInvite = useAcceptInvite();
  const { setUser } = UserContainer.useContainer();
  const { checkAddonLogin } = useAddonRedirect();
  const { isAddon } = AddonContainer.useContainer();
  const signOut = useSignOut();

  /**
   *
   * @param { email } : user email,
   * @param {password } : user password
   * @param { strategy } : to generate authentication token on basis of user(invited, local, etc...)
   * @param { token = '' } : i.e. if user is doing sign up via invitation link then this will send the token to backend for verification
   * @param { id = '' } : i.e. if user is doing sign up via invitation link then this will send the id to backend to generate token
   * @param {accessId = '' } : accessId i.e. if this is available then user should be already be a user of sparrow and has invitation link for a business
   * @param { businessId = ''} : businessId i.e. if this is available then user should be already be a user of sparrow and has invitation link
   */

  const authenticateUser = async ({
    email,
    password,
    strategy,
    token = '',
    id = '',
    accessId = '',
    businessId = '',
  }) => {
    const response = await API.authentication.create({
      email,
      password,
      strategy,
      token,
      id,
    });

    const { accessToken } = response;
    if (isAddon) {
      await checkAddonLogin(accessToken);
    }
    setCookie('accessToken', response.accessToken, {
      path: APP_ROUTE.ROOT,
    });

    // updateAccessToken(accessToken);
    if (strategy === 'invite') {
      const invitedResponse = await API.user.update(response.user.id, {
        password,
        onboarding: 'welcome',
        agreedTC: new Date().valueOf(),
      });

      await acceptInvite(accessId, businessId);

      // Sign out after accepting invitation instead of initializing app
      signOut(true);
      return response.user;
    }

    const updatedUser = response.user;
    // triggering auto accept invite if user is coming via invitation link
    // both values(accessId, businessId) should be available
    if (accessId && businessId) {
      await acceptInvite(accessId, businessId);
      // if (response.user.onboarding !== 'complete') {
      //   updatedUser = await API.user.update(response.user.id, {
      //     onboarding: 'complete',
      //   });
      // }
    }

    set(LOCAL_STORAGE_TYPES.USERID, response.user.id);
    if (response.user.invitationToken) {
      set(LOCAL_STORAGE_TYPES.INVITATION_TOKEN, {
        invitationToken: response.user.invitationToken,
      });
    }

    setUser(updatedUser);

    await initializeApp();

    return updatedUser;
  };

  return authenticateUser;
};

const useIsAuthenticated = () => {
  const [cookies] = useCookies();
  const { updateAccessToken } = AuthContainer.useContainer();

  const isAuthenticated = () => {
    const { accessToken } = cookies;

    const isAccessTokenAvailable = !!accessToken;
    if (isAccessTokenAvailable) {
      updateAccessToken(accessToken);
    }
    return isAccessTokenAvailable;
  };

  return isAuthenticated;
};

const useResetPassword = () => {
  const resetPassword = async email => {
    await API.authManagement.sendResetPasswordEmail(email);
  };
  return resetPassword;
};

const useUpdatePassword = () => {
  const navigate = useNavigate();
  const updatePassword = async ({ token, password }) => {
    await API.authManagement.updatePassword({ token, password });
    navigate(`/${APP_ROUTE.AUTH}/${APP_ROUTE.LOGIN}`, { replace: true });
  };
  return updatePassword;
};

const useChangePassword = () => {
  const changePassword = async ({ oldPassword, password }) => {
    await API.authManagement.changePassword({ oldPassword, password });
  };
  return changePassword;
};

const useAuthenticateExternalService = () => {
  const { updateAccessToken } = AuthContainer.useContainer();
  const { isAddon } = AddonContainer.useContainer();
  const { setUser } = UserContainer.useContainer();
  const [, setCookie] = useCookies(['accessToken']);
  const navigate = useNavigate();
  const location = useLocation();
  const getBusiness = useGetBusiness();
  const { checkAddonLogin } = useAddonRedirect();
  const isAddonPage = location.pathname.includes(`${APP_ROUTE.ADDON}`);

  const receiveAuthStatus = async event => {
    if (
      event.origin !== CLIENT_BASE_URL ||
      event.data?.type !== 'external-auth'
    ) {
      return;
    }

    const { accessToken } = event.data;
    if (accessToken != null) {
      // Update/set the latest access token
      if (isAddonPage) {
        await checkAddonLogin(accessToken);
      }
      setCookie('accessToken', accessToken, {
        path: APP_ROUTE.ROOT,
      });
      updateAccessToken(accessToken);

      // fetch the current logged in user
      const { data } = await API.user.get();
      setUser(data[0]);
      // set identity in our tracking libraries
      setIdentity({
        email: data[0].email,
        name: data[0].name,
        id: data[0].id,
        avatar: data[0].avatar,
      });
      const business = await getBusiness({
        fields: 'accounts',
      });

      // Store workspace information in localStorage
      if (!Number.isNaN(Number(business.data[0].id))) {
        localStorage.setItem(
          'workspace',
          JSON.stringify({
            client: business.data[0].id,
            name: business.data[0].name,
          })
        );
      }

      // checking if the event is from settings page
      if (location.pathname.includes('settings')) {
        toast({
          variant: 'success',
          title: 'Connected successfully',
        });
      }
      // Reinitialize the app
      // navigate(APP_ROUTE.ROOT, { replace: true });
    } else {
      // eslint-disable-next-line
      toast({
        variant: 'destructive',
        title:
          event.data.error ?? 'Authentication failed with external service.',
      });
    }
  };

  useEffect(() => {
    window.addEventListener('message', receiveAuthStatus);
    return () => {
      window.removeEventListener('message', receiveAuthStatus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authenticateExternalService = (providerName, extraParams = '') => {
    const authUrl = `${API.serverUrl}/oauth/${providerName}${extraParams}`;
    window.open(authUrl, 'googleLoginWindow', 'fullscreen=yes');
  };

  return authenticateExternalService;
};

const useEmailVerificationResend = () => {
  const { post } = useNetwork();
  const verifyEmail = async () => {
    const res = await post(`/${APP_ROUTE.RESEND_VERIFICATION_EMAIL}`);
    return res.msg;
  };

  return verifyEmail;
};

export {
  useAuthenticateUser,
  useSignOut,
  useIsAuthenticated,
  useResetPassword,
  useUpdatePassword,
  useChangePassword,
  useInitializeApp,
  useAuthenticateExternalService,
  useEmailVerificationResend,
};
